import {
  ADTType,
  DataEntityPresenterModel,
  DataEntryPresenterModel,
  DataRelationPresenterModel,
  DataTypeEnum,
  ENTITY_META_FIELDS,
  ENTRY_META_FIELDS,
  FieldPresenterModel,
  LocalizedValueModel,
  MappedFieldUpdateModeEnum,
  TEMP_FIELD_COMPONENT_NAMES,
} from '@osos/shared/types';
import { produce } from 'immer';

import {
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { intersection } from 'lodash-es';
import cloneDeep from 'lodash-es/cloneDeep';
import { GeneralUIViewConfiguration } from '../../models';
import { getPathValue } from '../get-path-value/get-path-value';
import { removeMarkdownTagsAndFormatTables } from '../remove-markdown-format-table-util';

export interface FieldUtilParams {
  entity: DataEntityPresenterModel;
  normalizedName?: string;
  id?: string;
  withEntryMetaFields?: boolean;
  withEntityMetaFields?: boolean;
}

export function filterFieldsByNormalizedNames(
  fields: FieldPresenterModel[],
  normalizedNames: string[],
): FieldPresenterModel[] {
  if (!normalizedNames) {
    return [];
  }

  function innerFun(
    fields: FieldPresenterModel[],
    blockName?: LocalizedValueModel,
  ): FieldPresenterModel[] {
    const _fields: FieldPresenterModel[] = [];
    fields.forEach((_field) => {
      _field = cloneDeep(_field);
      if (_field.dataType === DataTypeEnum.Block) {
        const _res = innerFun(_field.block.internalEntity.fields, _field.name);
        const _filteredFields = _res.filter((_inf) =>
          normalizedNames.some((_selectedField) => {
            const _arr = _selectedField.split('.');
            return (
              _arr[_arr.length - 1] === _inf.normalizedName &&
              _arr[_arr.length - 2] === _field.normalizedName
            );
          }),
        );
        if (_filteredFields.length) {
          _field.block.internalEntity.fields = [..._filteredFields];
          _fields.push(_field);
        }
      } else if (!blockName) {
        const _fieldExist = normalizedNames.some(
          (_f) => _f === _field.normalizedName,
        );
        if (_fieldExist) {
          _fields.push(_field);
        }
      } else {
        _fields.push(_field);
      }
    });
    return _fields;
  }

  return innerFun(fields);
}

export function getFieldByNormalizedName({
  entity,
  normalizedName,
  withEntryMetaFields,
  withEntityMetaFields,
}: FieldUtilParams): FieldPresenterModel {
  let lastField;
  let fields = entity.fields;
  const normalizedNames = normalizedName.split('.');
  normalizedNames.forEach((normalizedName) => {
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < fields.length; i++) {
      if (fields[i].normalizedName === normalizedName) {
        lastField = fields[i];
        fields = lastField.block?.internalEntity?.fields || [];
        break;
      }
    }
  });
  if (!lastField && withEntryMetaFields) {
    fields = ENTRY_META_FIELDS;
    lastField = getFieldFromFieldsByNormalizedName(fields, normalizedName);
  }

  if (!lastField && withEntityMetaFields) {
    fields = ENTITY_META_FIELDS;
    lastField = getFieldFromFieldsByNormalizedName(fields, normalizedName);
  }
  return lastField;
}

export function getFieldById(
  fields: FieldPresenterModel[],
  id,
): FieldPresenterModel {
  if (!fields?.length) return undefined;
  for (const field of fields) {
    if (field.id === id) {
      return field;
    }
    if (field.block?.internalEntity?.fields?.length) {
      const blockSubField = getFieldById(field.block.internalEntity.fields, id);
      if (blockSubField) return blockSubField;
    }
  }
  return undefined;
}
export function getFieldIdFromNormalizedName(
  entity: DataEntityPresenterModel,
  normalizedName: string,
): string {
  let lastField;
  let fields = entity.fields;
  const normalizedNames = normalizedName.split('.');
  normalizedNames.forEach((normalizedName) => {
    for (let i = 0; i < fields.length; i++) {
      if (fields[i].normalizedName == normalizedName) {
        lastField = fields[i];
        fields = lastField.block?.internalEntity?.fields || [];
        break;
      }
    }
  });
  return lastField?.id;
}

export function mapEntityFields(
  fields: FieldPresenterModel[],
  mapConditionFn: (field: FieldPresenterModel) => boolean,
  mapValueFn: (field: FieldPresenterModel) => FieldPresenterModel,
): FieldPresenterModel[] {
  return fields.reduce((acc, field) => {
    field = produce(field, (draft) => {
      if (field.block?.internalEntity?.fields.length > 0) {
        draft.block.internalEntity.fields = mapEntityFields(
          field.block.internalEntity.fields,
          mapConditionFn,
          mapValueFn,
        );
      }
    });

    if (mapConditionFn(field)) {
      field = mapValueFn(field);
    }

    acc.push(field);
    return acc;
  }, []);
}

export function getFieldNormalizedNameFromId(entity: any, id: string): string {
  let normalizedNames: string[] = [];
  for (let i = 0; i < entity.fields.length; i++) {
    if (entity.fields[i].id == id) {
      normalizedNames.push(entity.fields[i].normalizedName);
      break;
    }
    if (entity.fields[i]?.block?.internalEntity?.fields?.length) {
      normalizedNames = getInternalEntityNormalizedNameFromId(
        entity.fields[i],
        id,
      );
      if (normalizedNames?.length) break;
    }
  }
  return normalizedNames.join('.');
}

export function getInternalEntityNormalizedNameFromId(field, id): string[] {
  let normalizedNames = [];
  if (!field?.block?.internalEntity?.fields?.length) {
    if (field.id === id) {
      normalizedNames.push(field.normalizedName);
    }
    return normalizedNames;
  }
  const fields = field.block.internalEntity.fields;
  for (let i = 0; i < fields.length; i++) {
    normalizedNames = getInternalEntityNormalizedNameFromId(fields[i], id);
    if (normalizedNames.length > 0) {
      normalizedNames.unshift(field.normalizedName);
      break;
    }
  }
  return normalizedNames;
}

export function hasFieldADTSource(source: string): boolean {
  return Object.keys(ADTType)
    .filter((key) => typeof key === 'string')
    .includes(source);
}

export function hasFieldADTType(type: string): boolean {
  return Object.keys(ADTType)
    .filter((key) => typeof key === 'string')
    .includes(type);
}

export function getFieldFromFieldsByNormalizedName(
  fields: FieldPresenterModel[],
  normalizedName: string,
): FieldPresenterModel | undefined {
  let lastField;
  for (const field of fields) {
    if (field.normalizedName === normalizedName) {
      lastField = field;
      fields = lastField.block?.internalEntity?.fields || [];
      break;
    }
  }
  return lastField;
}

export function getFieldFromFieldsByCompoundNormalizedName(
  fields: FieldPresenterModel[],
  normalizedName: string,
): FieldPresenterModel | undefined {
  // let lastField = null;
  for (const field of fields) {
    if (field._normalizedName === normalizedName) {
      return field;
    }
    if (field?.block?.internalEntity?.fields?.length) {
      const innerField = getFieldFromFieldsByCompoundNormalizedName(
        field.block.internalEntity.fields,
        normalizedName,
      );
      if (innerField) {
        return innerField;
      }
    }
  }
  return undefined;
}

export function isSingularEntityReferenceField(
  field: FieldPresenterModel,
): boolean {
  return (
    !hasFieldMultiValue(field) &&
    !isFieldRepeatable(field) &&
    hasFieldEntitySource(field)
  );
}

export function isFieldRepeatable(field: FieldPresenterModel): boolean {
  return !!field?.options?.repeatable;
}

export function isFieldMultiValue(field: FieldPresenterModel): boolean {
  return !!field?.options?.multiValue;
}

export function isFieldRequired(field: FieldPresenterModel): boolean {
  return (
    !!field?.validations?.required?.enabled &&
    !field?.validations?.required?.expression
  );
}

export function isFieldBlock(field: FieldPresenterModel): boolean {
  return !!field?.block?.internalEntity;
}

export function isMappedField(field: FieldPresenterModel): boolean {
  return !!field?.mapping?.enabled;
}

export function isFieldDisabled(field: FieldPresenterModel): boolean {
  return (
    field.locked?.enabled ||
    field.options?.readonly ||
    field.options?.displayOnly?.enabled ||
    (isMappedField(field) &&
      (field.mapping.updateMode === MappedFieldUpdateModeEnum.Never ||
        field.mapping.updateMode === MappedFieldUpdateModeEnum.ParentOnly))
  );
}
export function isFieldBlob(field: FieldPresenterModel): boolean {
  return field?.dataType === DataTypeEnum.blob;
}
export function hasFieldMultipleMinLessOrEqualTo(
  field: FieldPresenterModel,
  target: number,
): boolean {
  return (
    !!hasFieldMultiValue(field, false) && field.options.multiValue.min <= target
  );
}

export function hasFieldRepeatableMinLessOrEqualTo(
  field: FieldPresenterModel,
  target: number,
): boolean {
  return (
    !!isFieldRepeatable(field) &&
    (field.options.repeatable?.min?.value as number) <= target
  );
}

export function hasFieldRepeatableMinToZero(
  field: FieldPresenterModel,
): boolean {
  return (
    !!isFieldRepeatable(field) && field.options.repeatable?.min?.value === 0
  );
}

/**
 * This either must be handled on component level or we need to make the options.isRange besides multi value
 */
export function hasFieldMultiValue(
  field: FieldPresenterModel,
  includeRange = true,
): boolean {
  return !!field?.options?.multiValue || (includeRange && hasFieldRange(field));
}

export function isFieldVirtual(field: FieldPresenterModel): boolean {
  return !!field?.virtualField?.enabled;
}

export function hasFieldMultiMinValueLargerOrEqualTo(
  field: FieldPresenterModel,
  targetSource,
): boolean {
  return (
    !!hasFieldMultiValue(field, false) &&
    field.options.multiValue.min >= targetSource
  );
}

export function hasFieldRepeatableMinValueLargerOrEqualTo(
  field: FieldPresenterModel,
  targetSource,
): boolean {
  return (
    isFieldRepeatable(field) &&
    field.options.repeatable?.min?.value >= targetSource
  );
}

export function getFieldMultiMinValue(field: FieldPresenterModel): number {
  return field?.options?.multiValue?.min;
}

export function getFieldMultiMaxValue(field: FieldPresenterModel): number {
  return field?.options?.multiValue?.max;
}

export function getFieldRepeatableMinValue(field: FieldPresenterModel): number {
  return field.options.repeatable?.min?.value as number;
}

export function hasFieldRange(field: FieldPresenterModel): boolean {
  return field?.renderOptions?.editComponent?.options?.isRanged;
}

export function findFieldByDataType(
  entityFields: FieldPresenterModel[],
  dataTypes: DataTypeEnum[],
  operator: 'AND' | 'OR' | 'NOT' = 'OR',
): FieldPresenterModel[] {
  const _returnedFields: FieldPresenterModel[] = [];
  function innerFun(fields: FieldPresenterModel[]): FieldPresenterModel[] {
    const _fields = [];
    if (fields && fields.length > 0) {
      // tslint:disable-next-line: prefer-for-of
      for (let index = 0; index < fields.length; index++) {
        const _field = fields[index];
        if (_field.dataType === DataTypeEnum.Block) {
          innerFun(_field.block?.internalEntity?.fields);
        } else {
          let exist = false;
          switch (operator) {
            case 'OR': {
              if (
                dataTypes.some((_dataType) => _dataType === _field.dataType)
              ) {
                exist = true;
              }
              break;
            }
            case 'NOT': {
              if (
                dataTypes.some((_dataType) => _dataType !== _field.dataType)
              ) {
                exist = true;
              }
              break;
            }
          }
          if (exist) {
            _fields.push(_field);
          }
        }
      }
    }
    _returnedFields.push(..._fields);
    return _returnedFields;
  }
  innerFun(entityFields);
  return _returnedFields;
}

export function findFieldByComponentName(
  entityFields: FieldPresenterModel[],
  componentName: string,
  componentType: 'editComponent' | 'viewComponent' = 'editComponent',
): FieldPresenterModel[] {
  const _returnedFields: FieldPresenterModel[] = [];
  function innerFun(fields: FieldPresenterModel[]): FieldPresenterModel[] {
    const _fields = [];
    if (fields && fields.length > 0) {
      // tslint:disable-next-line: prefer-for-of
      for (let index = 0; index < fields.length; index++) {
        const _field = fields[index];
        if (_field.dataType === DataTypeEnum.Block) {
        } else if (
          _field.renderOptions[componentType].component === componentName
        ) {
          _fields.push(_field);
        }
      }
    }
    _returnedFields.push(..._fields);
    return _returnedFields;
  }
  innerFun(entityFields);
  return _returnedFields;
}

export function findFieldBasedOnShowOption(
  entityFields: FieldPresenterModel[],
  /**
   * @TODO Use an showOptionEnum
   */
  showOption:
    | 'showAsTitle'
    | 'showAsSubTitle'
    | 'showAsEntryPicture'
    | 'showAsMainDate'
    | 'showAsCategory'
    | 'showAsSubCategory'
    | 'showAsRelationField'
    | 'showAsCaption'
    | 'showAsMainNumber'
    | 'showAsMainPhone',
): FieldPresenterModel {
  let stop = false;
  function innerFun(fields: FieldPresenterModel[]): FieldPresenterModel {
    let field: FieldPresenterModel = null;
    if (!stop && fields && fields.length > 0) {
      fields.forEach((_field) => {
        if (!stop) {
          if (_field.dataType === DataTypeEnum.Block) {
            const _res = innerFun(_field.block?.internalEntity?.fields);
            if (_res) {
              field = {
                ..._res,
              };
            }
          } else if (_field.options[showOption]) {
            stop = true;
            field = _field;
          }
        }
      });
    }
    return field;
  }
  return innerFun(entityFields);
}

/**
@description
  The getTreeFilteredFields function takes in an array of FieldPresenterModel objects,
  a filter function, and optional arguments and configuration object.
  It returns an array of FieldPresenterModel objects that pass the filter function, along with any child fields that also pass the filter function.
  @param {FieldPresenterModel[]} fields - An array of FieldPresenterModel objects to be filtered.
  @param {Function} filterFn - A function that takes a FieldPresenterModel object and optional arguments and returns a boolean. If the function returns true, the field is included in the filtered results.
  @param {any} [args=undefined] - Optional arguments to be passed to the filter function.
  @param {{ignoreBlockWhenFiltering: boolean}} [config={ignoreBlockWhenFiltering: true}] - An object that allows for configuration of the filtering process.
  The only property currently supported is ignoreBlockWhenFiltering, which defaults to true. If set to false,
  the function will include blocks in the filtered results even if the block itself does not pass the filter function.
  @returns {FieldPresenterModel[]} The function returns an array of FieldPresenterModel objects that pass the filter function, along with any child fields that also pass the filter function.
 */
export function getTreeFilteredFields(
  fields: FieldPresenterModel[],
  filterFn: Function,
  args: any | undefined = undefined,
  config: {
    ignoreBlockWhenFiltering: boolean;
  } = {
    ignoreBlockWhenFiltering: true,
  },
): FieldPresenterModel[] {
  if (!Array.isArray(fields)) {
    return [];
  }
  const filteredFields: FieldPresenterModel[] = [];
  for (const field of fields) {
    const currentField: FieldPresenterModel = JSON.parse(JSON.stringify(field));
    if (field.block?.internalEntity) {
      if (!config.ignoreBlockWhenFiltering && !filterFn(field, args)) {
        continue;
      }
      currentField.block.internalEntity.fields = getTreeFilteredFields(
        field.block.internalEntity.fields,
        filterFn,
        args,
        config,
      );
      if (currentField.block.internalEntity.fields.length) {
        filteredFields.push(currentField);
      }
      continue;
    }
    if (!filterFn(field, args)) continue;
    filteredFields.push(currentField);
  }
  return filteredFields;
}

// this function takes nested array of fields and return the  filtered result as flat
export function getFlattedFilteredFields(
  fields: FieldPresenterModel[],
  filterFn: Function,
  args: any | undefined = undefined,
): FieldPresenterModel[] {
  if (!Array.isArray(fields)) {
    return [];
  }
  return fields.reduce((acc, field) => {
    if (filterFn(field, args)) {
      acc.push(field);
    }
    if (field.block?.internalEntity) {
      const _filtered = getFlattedFilteredFields(
        field.block?.internalEntity.fields,
        filterFn,
        args,
      );
      acc.push(..._filtered);
    }

    return acc;
  }, []);
}
export function isFieldLocked(field: FieldPresenterModel): boolean {
  return !!field.locked?.enabled;
}

export function hasFieldSource(field: FieldPresenterModel): boolean {
  return (
    (!!field.source && !field.sources?.length) || field.sources?.length === 1
  );
}

export function hasSharedSources(...fields: FieldPresenterModel[]) {
  const sources = fields.map((d) => d.sources.map((s) => s.entity));
  const intersections = intersection(...sources);
  return intersections.length > 0;
}

export function hasFieldEntitySource(field: FieldPresenterModel): boolean {
  return (
    hasFieldSource(field) &&
    !hasFieldADTSource(field.source)
  );
}

export function hasReferenceWithEntity(
  field: FieldPresenterModel,
  entity: DataEntityPresenterModel | string,
) {
  const entityId = typeof entity === 'string' ? entity : entity.id;
  return (
    field.sources?.length && field.sources.some((s) => s.entity === entityId)
  );
}

export function hasFieldBlobType(field: FieldPresenterModel): boolean {
  return field.dataType === DataTypeEnum.blob;
}

export function getBlobDisplayedFields(
  entity: DataEntityPresenterModel,
  config: GeneralUIViewConfiguration,
): FieldPresenterModel[] {
  const fields = config?.getDataView()
    ? getFlattedFilteredFields(entity.fields, (field) =>
        config.getDataView().fields.includes(field._normalizedName),
      )
    : entity.fields;
  return getFlattedFilteredFields(fields, hasFieldBlobType);
}

export const FIELD_CRITERIA_FULL_MATCH = {
  type: true,
  source: true,
  componentName: true,
};

export const FIELD_TYPE_MATCH = {
  type: true,
  source: false,
  componentName: false,
};

export const FIELD_TYPE_SOURCE_MATCH = {
  type: true,
  source: true,
  componentName: false,
};

export function doesFieldMatch(
  field: FieldPresenterModel,
  currentField: FieldPresenterModel,
  matchCriteria: {
    type?: boolean;
    source?: boolean;
    componentName: boolean;
  } = FIELD_CRITERIA_FULL_MATCH,
): boolean {
  if (matchCriteria?.type && field.dataType !== currentField?.dataType) {
    return false;
  }

  if (matchCriteria?.source && field.source !== currentField?.source) {
    return false;
  }

  if (
    matchCriteria?.componentName &&
    field.renderOptions?.editComponent?.component !==
      currentField?.renderOptions?.editComponent?.component
  ) {
    return false;
  }

  return true;
}

export function modifyFields({
  fields,
  modifierPredicate,
  modifier,
  modifyConfig = {
    modifyFirstLevel: false,
  },
}: {
  fields: Array<FieldPresenterModel>;
  modifierPredicate: (field: FieldPresenterModel) => boolean;
  modifier: (field: FieldPresenterModel) => void;
  modifyConfig?: {
    modifyFirstLevel: boolean;
  };
}): Array<FieldPresenterModel> {
  return fields.reduce((acc, field) => {
    let newField: FieldPresenterModel;
    if (modifierPredicate(field) || isFieldBlock(field)) {
      newField = produce(field, (draft) => {
        modifier(draft);
        if (
          !modifyConfig?.modifyFirstLevel &&
          isFieldBlock(field) &&
          field.block?.internalEntity?.fields?.length
        ) {
          draft.block.internalEntity.fields = modifyFields({
            fields: field.block.internalEntity.fields,
            modifier,
            modifierPredicate,
            modifyConfig,
          });
        }
      });
    } else {
      newField = field;
    }

    acc.push(newField);
    return acc;
  }, []);
}

// Return the field and it's root parent
// The root parent will only contains the parents that lead to this fields
// To track all the parents we have
// EX BlockGrandParent[internalEntities.fields] => BlockParent[internalEntities.fields] => [field]
//
//
export function getTargetFieldAndRootParent(
  fields: Array<FieldPresenterModel>,
  fieldId,
): {
  field: FieldPresenterModel;
  block: FieldPresenterModel;
} {
  let targetedField: FieldPresenterModel;
  let targetBlockField: FieldPresenterModel;
  const targetedBlockOrField = getTreeFilteredFields(
    fields,
    (field) => field.id === fieldId,
  )?.[0];
  if (!targetedBlockOrField) return null;

  if (targetedBlockOrField.dataType === DataTypeEnum.Block) {
    targetBlockField = targetedBlockOrField;
    targetedField = targetedBlockOrField.block?.internalEntity?.fields?.[0];
  } else {
    targetedField = targetedBlockOrField;
  }

  return {
    field: targetedField,
    block: targetBlockField || null,
  };
}

export function setRepeatableFieldFormValue(
  field: FieldPresenterModel,
  form: UntypedFormGroup,
  value: any[],
  useFullNormalizedName = true,
) {
  const formArray = form.get(
    useFullNormalizedName ? field._normalizedName : field.normalizedName,
  ) as UntypedFormArray;
  if (!formArray) return;
  formArray.clear();
  value.forEach((fieldValue) => {
    const _control = new UntypedFormControl(fieldValue);
    formArray.push(_control);
  });
}

export function getMainTitleFieldPath(
  path: string,
  fields: FieldPresenterModel[],
): string | null {
  if (!fields || !fields.length) return null;
  for (const f of fields) {
    if (f.dataType === DataTypeEnum.Block) {
      const mainTitle = getMainTitleFieldPath(
        `${path}${!path ? '' : '.'}${f.normalizedName}`,
        f.block.internalEntity.fields,
      );
      if (mainTitle) return mainTitle;
      continue;
    }

    if (f.options.showAsTitle) {
      return `${path}${!path ? '' : '.'}${f.normalizedName}`;
    }
  }

  return null;
}

/**
 * Helper function to check if a field is a reference field (RelationComponent)
 */
export function isReferenceField(field: FieldPresenterModel): boolean {
  return (
    field?.dataType === DataTypeEnum.Entity &&
    field?.renderOptions?.editComponent?.component === 'RelationComponent'
  );
}

/**
 * Helper function to check if a field is a category field
 */
export function isCategoryField(field: FieldPresenterModel): boolean {
  return (
    field?.dataType === DataTypeEnum.Json &&
    field?.renderOptions?.editComponent?.component ===
      TEMP_FIELD_COMPONENT_NAMES.categoryComponent
  );
}

/**
 * Helper function to check if a field is a radio field
 */
export function isRadioField(field: FieldPresenterModel): boolean {
  return (
    field?.dataType === DataTypeEnum.Json &&
    field?.renderOptions?.editComponent?.component ===
      TEMP_FIELD_COMPONENT_NAMES.switchComponent &&
    field?.renderOptions?.editComponent?.options?.switchType === 2 &&
    !!field?.dataCollection
  );
}

/**
 * Helper function to check if a field is an ADT field
 */
export function isADTField(field: FieldPresenterModel): boolean {
  const ADT = Object.values(ADTType);
  return (
    isReferenceField(field) && !!field?.source && ADT.includes(field.source)
  );
}

export function isUserField(field: FieldPresenterModel): boolean {
  return (
    field?.dataType === DataTypeEnum.Entity &&
    field?.renderOptions?.editComponent?.component === 'UserReferenceComponent'
  );
}

/**
 * Helper function to check if a field is a reference ADT field
 */
export function isReferenceADTField(field: FieldPresenterModel): boolean {
  return isReferenceField(field) && isADTField(field);
}

export function isReferenceNoneADTField(field: FieldPresenterModel): boolean {
  return (isReferenceField(field) || isUserField(field)) && !isADTField(field);
}

export function getMainTitleFieldValue(
  entity: DataEntityPresenterModel,
  entry: DataEntryPresenterModel,
  typesUtilsService,
): string | null {
  if (!entity || !entry) return null;
  const mainTitlePath = getMainTitleFieldPath('', entity.fields);
  if (!mainTitlePath) return null;
  const value = getPathValue(entry.value, mainTitlePath) ?? '';

  // Get the main title field to check its data type
  const mainTitleField = findFieldBasedOnShowOption(
    entity.fields,
    'showAsTitle',
  );

  // Handle array values
  let processedValue = Array.isArray(value) && value?.length ? value[0] : value;

  // Handle different field types with different _resolved paths
  if (mainTitleField && processedValue?._resolved) {
    // Category and Radio fields use _resolved.label
    if (isCategoryField(mainTitleField) && processedValue?._resolved?.label) {
      processedValue = processedValue._resolved.label;
    }
    // Reference fields (Entity type) and ADT fields use _resolved.mainTitle
    else if (
      (isReferenceNoneADTField(mainTitleField) ||
        isADTField(mainTitleField) ||
        isRadioField(mainTitleField)) &&
      processedValue?._resolved?.mainTitle
    ) {
      processedValue = processedValue._resolved.mainTitle;
    }
  }

  return removeMarkdownTagsAndFormatTables(
    typesUtilsService.getLocaleString(processedValue),
  );
}

export function filterOutDuplicateTargetFields(
  relations: DataRelationPresenterModel[],
): DataRelationPresenterModel[] {
  if (!relations?.length) return relations ?? [];
  const counts = new Map<string, number>();

  const makeKey = (r: DataRelationPresenterModel) => {
    const fieldId = r?.targetField?.id ?? r?.targetField?.normalizedName ?? '';
    const key = `${fieldId}`;
    return key && key !== ':' ? key : '';
  };

  for (const r of relations) {
    const k = makeKey(r);
    if (!k) continue; // if we cannot identify the target field, don't count it as a duplicate key
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }

  return relations.filter((r) => {
    const k = makeKey(r);
    if (!k) return true; // keep items without a resolvable key
    return (counts.get(k) ?? 0) === 1; // keep only unique keys
  });
}
